import React, { useEffect, useState, memo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Box } from "@mui/material";
import { ReactComponent as Loader } from "../../../assets/icons/loader.svg";
import { ContainedButton } from "../../Buttons/ContainedButton";

import {
  Container,
  FailedIcon,
  TitleContainer,
  Title,
  Subtitle,
  FormActions,
  dialogButtonContainer,
  dialogContent,
  Result,
  loader
} from "../styled";
import { useTrackingHistory } from "../../../utils/useTrackingHistory";
import { ConfirmDialog } from "../../../components/Modals";
import {
  sendProspectToAPIPromisify as sendProspectToApi,
  resetScreeningError
} from "../../../store/actions/sendProspectToAPI";
import { resetProspect, updateProspect } from "../../../store/actions/appConfig";
import { getApplyForAccount } from "../../../store/selectors/appConfig";
import { CONTINUE } from "../../../constants";
import { ContinueButton } from "../../../components/Buttons/ContinueButton";
import { Layout } from "../../../components/v2/Layout";
import { Footer } from "../../../components/v2/Footer/Footer";
import { useMediaQuery } from "@mui/system";
import { Icon, ICONS } from "../../../components/Icons";

import routes from "../../../routes";

const blockConfirmSchema = Yup.object({
  accountOpening: Yup.boolean()
});

/**
 * Renders the action buttons (Yes/No) or a loader.
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether the loading state is active.
 * @param {Function} props.setFieldValue - Formik's setFieldValue function.
 * @returns {React.ReactElement}
 */
const AccountOpeningActions = ({ isLoading, setFieldValue }) => {
  const dispatch = useDispatch();
  const pushHistory = useTrackingHistory();

  const handleNoClick = () => {
    dispatch(resetScreeningError());
    dispatch(resetProspect());
    pushHistory(routes.financeProductComparison, true);
  };

  if (isLoading) {
    return <Loader sx={loader} alt="loading" />;
  }

  return (
    <FormActions sx={{ display: "flex", gap: 2 }}>
      <ContainedButton
        variant="outlined"
        handleClick={handleNoClick}
        data-testid="button-no"
        label="Not now"
      />
      <ContainedButton
        handleClick={() => {
          setFieldValue("accountOpening", true);
          dispatch(updateProspect({ "prospect.applicationInfo.applyForAccount": true }));
        }}
        data-testid="button-yes"
        label="Yes, please"
      />
    </FormActions>
  );
};

AccountOpeningActions.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired
};

/**
 * DeclinedStatus Component
 * Displays the declined status screen when a user is declined for financing
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.financeType - Type of finance being applied for
 * @param {string} [props.screeningReason=""] - Reason for the screening outcome
 * @param {string} [props.icon=""] - Icon to display on the screen
 * @returns {React.ReactElement} Rendered component
 */
export const DeclinedStatus = ({ financeType, screeningReason = "" }) => {
  const dispatch = useDispatch();
  const pushHistory = useTrackingHistory();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const applyForAccount = useSelector(getApplyForAccount);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (applyForAccount === true) {
      setIsLoading(true);
      dispatch(sendProspectToApi(CONTINUE))
        .then(() => {
          setShowPrompt(true);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [applyForAccount, dispatch]);

  const handleOkClick = () => {
    dispatch(resetScreeningError());
    dispatch(resetProspect());
    pushHistory(routes.financeProductComparison, true);
  };

  const dialogOptions = (
    <Box sx={dialogButtonContainer}>
      <ContinueButton label="OK" onClick={handleOkClick} />
    </Box>
  );

  const dialogMessage = (
    <Box sx={dialogContent}>
      Thank you for your interest. Our team will contact you within 2 working days to assist you
      further. Thank you for choosing RAKBANK.
    </Box>
  );

  return (
    <>
      <Layout
        style={{
          mt: {
            xs: 1,
            sm: 7.5
          },
          mb: {
            xs: 22.5,
            sm: 10
          }
        }}
      >
        <Container>
          <FailedIcon>
            <Icon name={ICONS.cancel} />
          </FailedIcon>
          <TitleContainer>
            <Title>Sorry! You are not eligible</Title>
            <Result>
              <Subtitle
                sx={{
                  lineHeight: {
                    xs: "20px !important",
                    sm: "28px !important"
                  }
                }}
              >
                We regret that we are unable to offer you RAKBANK {financeType} Finance as the
                details provided do not meet the Bank's policy.
              </Subtitle>
              <Subtitle>Thank you for your interest in RAKBANK {financeType} Finance.</Subtitle>
              {screeningReason === "Loan Decline" && (
                <Subtitle>
                  However since you qualify for a Business current account, would you like to
                  proceed? If yes, please select the below checkbox.
                </Subtitle>
              )}
            </Result>
          </TitleContainer>
          {screeningReason === "Loan Decline" && (
            <Formik
              initialValues={{
                accountOpening: false
              }}
              onSubmit={() => {}}
              validationSchema={blockConfirmSchema}
              validateOnChange={false}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  {isMobile ? (
                    <Footer dataTestId="footer">
                      <AccountOpeningActions isLoading={isLoading} setFieldValue={setFieldValue} />
                    </Footer>
                  ) : (
                    <AccountOpeningActions isLoading={isLoading} setFieldValue={setFieldValue} />
                  )}
                </Form>
              )}
            </Formik>
          )}
        </Container>
      </Layout>
      <ConfirmDialog
        title={null}
        actions={dialogOptions}
        isOpen={showPrompt}
        message={dialogMessage}
        data-testid="confirm-dialog"
      />
    </>
  );
};

DeclinedStatus.propTypes = {
  /** Type of finance being applied for */
  financeType: PropTypes.string.isRequired,
  /** Reason for the screening outcome */
  screeningReason: PropTypes.string
};

export default memo(DeclinedStatus);
